import Controller from '@ember/controller';
import { action } from '@ember/object';
import { ajax } from 'discourse/lib/ajax';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { TEST_TYPES, REGIONS, PROTOCOLS, BLANK_NEW_RUN } from '../../lib/benchmark-constants';

export default class AdminPluginsProxyviewsBenchmarksController extends Controller {
  @service router;
  @service dialog;

  @tracked runs = [];
  @tracked meta = {};
  @tracked page = 1;
  @tracked loading = false;
  @tracked message = null;
  @tracked savingId = null;
  @tracked allProviders = [];
  @tracked runSearch = '';
  @tracked stats = null;

  @tracked showNewRunForm = false;
  @tracked newRun = { ...BLANK_NEW_RUN };

  @tracked editingRunId = null;
  @tracked runDraft = {};

  @tracked activeSection = 'runs';
  @tracked alerts = [];
  @tracked alertsMeta = {};
  @tracked alertsPage = 1;
  @tracked alertsLoading = false;

  testTypes = TEST_TYPES;
  regions = REGIONS;
  protocols = PROTOCOLS;

  get filteredRuns() {
    if (!this.runSearch) return this.runs;
    const q = this.runSearch.toLowerCase();
    return this.runs.filter(
      (r) =>
        (r.provider_name && r.provider_name.toLowerCase().includes(q)) ||
        (r.provider_slug && r.provider_slug.toLowerCase().includes(q)),
    );
  }

  get totalPages() {
    return this.meta.total_pages || 1;
  }

  get hasPrevPage() {
    return this.page > 1;
  }

  get hasNextPage() {
    return this.page < this.totalPages;
  }

  async loadStats() {
    try {
      const data = await ajax('/admin/plugins/proxyviews-benchmarks/stats');
      this.stats = data.stats || null;
    } catch {
      this.stats = null;
    }
  }

  async loadRuns() {
    this.loading = true;
    try {
      const data = await ajax('/admin/plugins/proxyviews-benchmarks/runs', {
        data: { page: this.page },
      });
      this.runs = data.benchmark_runs || [];
      this.meta = data.meta || {};
      await this.loadStats();
    } catch (err) {
      this.message = { text: 'Failed to load benchmark runs.', type: 'danger' };
    } finally {
      this.loading = false;
    }
  }

  @action
  dismissMessage() {
    this.message = null;
  }

  @action
  updateRunSearch(event) {
    this.runSearch = event.target.value;
  }

  @action
  handleFormKeydown(event) {
    if (event.key === 'Escape' && this.showNewRunForm) {
      this.showNewRunForm = false;
    }
  }

  @action
  nextPage() {
    if (!this.hasNextPage || this.loading) {
      return;
    }
    this.page += 1;
    this.loadRuns();
    this.scrollToTable();
  }

  @action
  prevPage() {
    if (!this.hasPrevPage || this.loading) {
      return;
    }
    this.page -= 1;
    this.loadRuns();
    this.scrollToTable();
  }

  @action
  toggleNewRunForm() {
    this.showNewRunForm = !this.showNewRunForm;
  }

  @action
  updateNewRunField(field, event) {
    this.newRun = { ...this.newRun, [field]: event.target.value };
  }

  @action
  async createRun() {
    if (!this.newRun.provider_id) {
      this.message = { text: 'Pick a provider.', type: 'danger' };
      return;
    }
    this.savingId = 'new-run';
    this.message = null;
    try {
      const result = await ajax('/admin/plugins/proxyviews-benchmarks/runs', {
        type: 'POST',
        data: {
          benchmark_run: {
            provider_id: parseInt(this.newRun.provider_id, 10),
            test_type: this.newRun.test_type,
            region: this.newRun.region,
            protocol: this.newRun.protocol,
            concurrent_connections: parseInt(this.newRun.concurrent_connections, 10) || 1,
            total_requests: parseInt(this.newRun.total_requests, 10) || 0,
            successful_requests: parseInt(this.newRun.successful_requests, 10) || 0,
            avg_response_time_ms: parseFloat(this.newRun.avg_response_time_ms) || null,
          },
        },
      });
      this.runs = [result.benchmark_run, ...this.runs];
      this.newRun = { ...BLANK_NEW_RUN };
      this.showNewRunForm = false;
      this.message = { text: 'Benchmark run created.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = {
        text: err.responseJSON?.errors?.join(', ') || 'Failed to create run.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  startEditRun(run) {
    this.runDraft = {
      test_type: run.test_type,
      region: run.region,
      protocol: run.protocol,
      concurrent_connections: run.concurrent_connections,
      total_requests: run.total_requests,
      successful_requests: run.successful_requests,
      avg_response_time_ms: run.avg_response_time_ms,
    };
    this.editingRunId = run.id;
  }

  @action
  cancelEditRun() {
    this.editingRunId = null;
    this.runDraft = {};
  }

  @action
  updateRunDraft(field, event) {
    const val = event.target.type === 'number' ? Number(event.target.value) : event.target.value;
    this.runDraft = { ...this.runDraft, [field]: val };
  }

  @action
  async saveRun(run) {
    this.savingId = 'run-' + run.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-benchmarks/runs/${run.id}`, {
        type: 'PUT',
        data: {
          benchmark_run: {
            test_type: this.runDraft.test_type,
            region: this.runDraft.region,
            protocol: this.runDraft.protocol,
            concurrent_connections: parseInt(this.runDraft.concurrent_connections, 10) || 1,
            total_requests: parseInt(this.runDraft.total_requests, 10) || 0,
            successful_requests: parseInt(this.runDraft.successful_requests, 10) || 0,
            avg_response_time_ms: parseFloat(this.runDraft.avg_response_time_ms) || null,
          },
        },
      });
      // Patch the live row from the draft
      this.runs = this.runs.map((r) => (r.id === run.id ? { ...r, ...this.runDraft } : r));
      this.editingRunId = null;
      this.runDraft = {};
      this.message = { text: `Saved run #${run.id}.`, type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = {
        text: err.responseJSON?.errors?.join(', ') || 'Failed to save run.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  deleteRun(run) {
    this.dialog.yesNoConfirm({
      message: `Delete benchmark run #${run.id}? This cannot be undone.`,
      didConfirm: async () => {
        this.savingId = 'delete-' + run.id;
        this.message = null;
        try {
          await ajax(`/admin/plugins/proxyviews-benchmarks/runs/${run.id}`, { type: 'DELETE' });
          this.runs = this.runs.filter((r) => r.id !== run.id);
          this.message = { text: `Deleted run #${run.id}.`, type: 'success' };
          this.clearMessageAfterDelay();
        } catch (err) {
          this.message = { text: 'Failed to delete run.', type: 'danger' };
        } finally {
          this.savingId = null;
        }
      },
    });
  }

  clearMessageAfterDelay() {
    this._messageTimer = setTimeout(() => {
      this.message = null;
      this._messageTimer = null;
    }, 4000);
  }

  // ── Alerts ─────────────────────────────────────────────────────────────

  @action
  setSection(section) {
    this.activeSection = section;
    this.message = null;
  }

  async loadAlerts() {
    this.alertsLoading = true;
    try {
      const data = await ajax('/admin/plugins/proxyviews-benchmarks/alerts', {
        data: { page: this.alertsPage },
      });
      this.alerts = data.alerts || [];
      this.alertsMeta = data.meta || {};
    } catch (err) {
      this.message = { text: 'Failed to load alerts.', type: 'danger' };
    } finally {
      this.alertsLoading = false;
    }
  }

  get alertsTotalPages() {
    return this.alertsMeta.total_pages || 1;
  }

  @action
  nextAlertsPage() {
    if (this.alertsPage >= this.alertsTotalPages || this.alertsLoading) return;
    this.alertsPage += 1;
    this.loadAlerts();
    this.scrollToTable();
  }

  @action
  prevAlertsPage() {
    if (this.alertsPage <= 1 || this.alertsLoading) return;
    this.alertsPage -= 1;
    this.loadAlerts();
    this.scrollToTable();
  }

  @action
  async toggleAlertActive(alert) {
    this.savingId = 'alert-toggle-' + alert.id;
    this.message = null;
    try {
      const result = await ajax(`/admin/plugins/proxyviews-benchmarks/alerts/${alert.id}/toggle`, {
        type: 'PUT',
      });
      this.alerts = this.alerts.map((a) => (a.id === alert.id ? result.alert : a));
    } catch (err) {
      this.message = { text: 'Failed to toggle alert.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  updateAlertThreshold(alert, event) {
    alert.threshold = parseFloat(event.target.value);
  }

  @action
  async saveAlertThreshold(alert) {
    this.savingId = 'alert-save-' + alert.id;
    this.message = null;
    try {
      const result = await ajax(`/admin/plugins/proxyviews-benchmarks/alerts/${alert.id}`, {
        type: 'PUT',
        data: { alert: { threshold: alert.threshold } },
      });
      this.alerts = this.alerts.map((a) => (a.id === alert.id ? result.alert : a));
      this.message = { text: 'Alert threshold updated.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = {
        text: err.responseJSON?.errors?.join(', ') || 'Failed to update threshold.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  deleteAlert(alert) {
    this.dialog.yesNoConfirm({
      message: `Delete alert "${alert.human_condition}" for ${alert.provider?.name || 'this provider'}?`,
      didConfirm: async () => {
        this.savingId = 'alert-delete-' + alert.id;
        this.message = null;
        try {
          await ajax(`/admin/plugins/proxyviews-benchmarks/alerts/${alert.id}`, { type: 'DELETE' });
          this.alerts = this.alerts.filter((a) => a.id !== alert.id);
          this.message = { text: 'Alert deleted.', type: 'success' };
          this.clearMessageAfterDelay();
        } catch (err) {
          this.message = { text: 'Failed to delete alert.', type: 'danger' };
        } finally {
          this.savingId = null;
        }
      },
    });
  }

  @action
  purgeStaleAlerts() {
    this.dialog.yesNoConfirm({
      message: 'Purge all inactive alerts older than 90 days? This cannot be undone.',
      didConfirm: async () => {
        this.savingId = 'purge-stale';
        this.message = null;
        try {
          const result = await ajax('/admin/plugins/proxyviews-benchmarks/alerts/purge-stale', {
            type: 'POST',
          });
          this.message = { text: result.message || 'Purge complete.', type: 'success' };
          this.clearMessageAfterDelay();
          await this.loadAlerts();
        } catch (err) {
          this.message = { text: 'Failed to purge stale alerts.', type: 'danger' };
        } finally {
          this.savingId = null;
        }
      },
    });
  }

  scrollToTable() {
    const el = document.querySelector('.pd-admin-table-container');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
